import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@repo/ui";
import { fileApi } from "~/api";
import type { Task } from "~/types/api";
import type { TaskManagerDTO } from "~/types/api-custom";

export const getTasksQueryKey = () => fileApi.queryOptions("get", "/api/Task").queryKey;
const getTasksByBrandKey = (brandId: number) => [...getTasksQueryKey(), "brand", brandId] as const;

const fetchTasksByBrand = async (brandId: number) => {
  if (!brandId) return [];

  const response = await fileApi.client.GET("/api/Task");

  if (response.error) {
    const errorMessage = `Failed to fetch tasks: ${response.error.toString()}`;
    throw new Error(errorMessage);
  }

  const allTasks = response.data || [];

  return allTasks.filter((task) => task.brandId === brandId);
};

export const useTasks = (enabled = true) => {
  const { showToast } = useToast();
  const { data, isLoading, error, refetch } = useQuery<Task[]>({
    queryKey: getTasksQueryKey(),
    queryFn: async () => {
      const response = await fileApi.client.GET("/api/Task");

      if (response.error) {
        const errorMessage = `Failed to fetch tasks: ${response.error.toString()}`;
        showToast("error", "Error", errorMessage);
        throw new Error(errorMessage);
      }

      return response.data || [];
    },
    enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    tasks: data || [],
    isLoading,
    error,
    refetch,
  };
};

export const useTasksByBrand = (brandId: number | undefined, enabled = true) => {
  const { showToast } = useToast();

  const { data, isLoading, error, refetch } = useQuery<TaskManagerDTO[]>({
    queryKey: brandId ? getTasksByBrandKey(brandId) : [...getTasksQueryKey(), "brand", "none"],
    queryFn: async () => {
      try {
        return await fetchTasksByBrand(brandId!);
      } catch (err) {
        const errorMessage = `Failed to fetch tasks for brand: ${(err as Error).message}`;
        showToast("error", "Error", errorMessage);
        throw err;
      }
    },
    enabled: enabled && !!brandId,
    staleTime: 3 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    tasks: data || [],
    isLoading,
    error,
    refetch,
  };
};

export const usePrefetchTasksByBrand = () => {
  const queryClient = useQueryClient();

  const prefetchTasks = async (brandId: number) => {
    await queryClient.prefetchQuery({
      queryKey: getTasksByBrandKey(brandId),
      queryFn: () => fetchTasksByBrand(brandId),
      staleTime: 3 * 60 * 1000,
    });
  };

  return { prefetchTasks };
};
